import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Calendar, globalizeLocalizer } from "react-big-calendar";
import globalize from 'globalize';
import DOMPurify from 'dompurify';
import Agenda from './components/agenda';
import Event from './components/event'; 
import ShowMore from './components/showmore'; 
import Toolbar from './components/toolbar'; 
import { formatDate } from './utils/dateutils';

const siteUrl = frontend_oec_object.siteurl || window.location.origin;
const EVENTS_API_URL = `${siteUrl}/wp-json/oec/v1/events`;

/**
 * Higher-order component to add drilldown view to a component
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} - Wrapped component with drilldown view
 */
const withDrilldownView = (Component) => {
  return (props) => <Component {...props} drilldownView="agenda" />;
};

/**
 * Higher-order component to add drilldown view along with showMore click handler to a component
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} - Wrapped component with drilldown view
 */
const withDrilldownShowMore = (Component, onShowMoreClickListener) => {
	return (props) => <Component {...props} drilldownView="agenda" onShowMoreClickListener={onShowMoreClickListener} />;
  };

/**
 * Higher-order component to add categories and filter functionality
 * @param {React.Component} Component - Component to wrap
 * @param {Array} categories - Categories for filtering
 * @param {Object} filters - Current filter settings
 * @param {Function} handleFilterAction - Handler for filter actions
 * @returns {React.Component} - Wrapped component with filtering functionality
 */
const withCategories = (Component, categories, filters, handleFilterAction) => {
  return (props) => <Component {...props} categories={categories} initialFilters={filters} onFilterAction={handleFilterAction} />;
};

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [renderEvents, setRenderEvents] = useState([]);
  const [filters, setFilters] = useState({ viewAll: true, categories: [], queryParamFilters: [] });
  const [categories, setCategories] = useState([]);
  const [culture, setCulture] = useState('en');
  const [messages, setMessages] = useState(null);
  const [date, setDate] = useState(new Date());
  const calendarRef = useRef(null); // Ref to access the calendar DOM element

  const localizer = globalizeLocalizer(globalize);
  const translations = window.OEC_Translations;

  /**
   * Handler for filter actions
   * @param {Object} filterAction - Filter action object containing filter data
   */
  const handleFilterAction = (filterAction) => {
    setFilters({ ...filterAction });
    if (filterAction.viewAll) {
      setRenderEvents(events);
    } else {
      const filteredEvents = events.filter(event =>
        filterAction.categories.some(cat => cat.event_category_name === event.category)
      );
      setRenderEvents(filteredEvents);
    }
  };

  /**
   * Handler for showmore click
   * @param {Date} slotDate - The slotdate that was selected
   */
  const onShowMoreClickListener = (slotDate) => {
    setDate(slotDate);
  };

  /**
   * Get categories filter from the URL query string
   * @returns {Array} - Array of category names
   */
  const getCategoryFilterFromUrl = () => {
    const sanitizedSearch = DOMPurify.sanitize(window.location.search);
    const urlParams = new URLSearchParams(sanitizedSearch);
    const categoriesParam = urlParams.get('categories');
    return categoriesParam ? categoriesParam.split(',') : [];
  };

  const { defaultDate, components, views, formats } = useMemo(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      defaultDate: firstDayOfMonth,
      components: {
        showMore: withDrilldownShowMore(ShowMore, onShowMoreClickListener), // Wrap ShowMore with the HOC,
        month: {
          event: withDrilldownView(Event),
        },
        toolbar: withCategories(Toolbar, categories, filters, handleFilterAction),
      },
      views: {
        month: true,
        week: false,
        day: false,
        agenda: Agenda,
      },
      formats: {
        agendaHeaderFormat: ({ start, end }, culture, localizer) =>
          localizer.format(start, "Y", culture),
      },
    };
  }, [categories]);

  /**
   * Handler for event selection
   * @param {Object} calEvent - The event that was selected
   */
  const onSelectEvent = useCallback((calEvent) => {
    setDate(calEvent.start);
  }, []);

  /**
   * Handler for date navigation
   * @param {Date} newDate - The new selected date
   */
  const onNavigate = useCallback((newDate) => {
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();
    const newMonth = newDate.getMonth();
    const newYear = newDate.getFullYear();

    if (currentMonth !== newMonth || currentYear !== newYear) {
      const formattedDate = formatDate(newDate);
      fetchEvents(EVENTS_API_URL, formattedDate, setEvents, filters, setRenderEvents);
    }
    setDate(newDate);
    triggetCalculateEventHeight();
  }, [date, filters, renderEvents, events]);

  /**
   * Fetch events from the API
   * @param {string} url - The API URL
   * @param {string} formattedDate - The formatted date for the query
   * @param {Function} setEvents - Function to set the fetched events
   * @param {Object} filters - Current filter settings
   * @param {Function} setRenderEvents - Function to set the filtered events
   */
  const fetchEvents = (url, formattedDate, setEvents, filters, setRenderEvents) => {
    setTimeout(() => {
      jQuery('.loading-overlay')?.addClass('is-active');
    }, 0);

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-WP-Nonce': frontend_oec_object.nonce,
      },
      body: new URLSearchParams({ date: formattedDate }),
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 403) {
            // Handle nonce verification failure
            setEvents([]);
            setRenderEvents([]);
            setCategories([]);
            jQuery('.loading-overlay')?.removeClass('is-active');
            throw new Error('Nonce verification failed');
          }
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setEvents(data.events);
        if (filters.viewAll) {
          setRenderEvents(data.events);
        } else {
          const filteredEvents = data.events.filter(event =>
            filters.categories.some(cat => cat.event_category_name === event.category)
          );
          setRenderEvents(filteredEvents);
        }
        setCategories(data.event_categories);
        Agenda.columnOrder = data.column_order  ;
        jQuery('.loading-overlay')?.removeClass('is-active');
      })
      .catch((error) => {
        jQuery('.loading-overlay')?.removeClass('is-active');
        setEvents([]);
        setRenderEvents([]);
        setCategories([]);
      });
  };

  /**
   * Format events into the format required by the calendar
   * @param {Array} events - Array of event objects to format
   * @returns {Array} - Array of formatted events
   */
  const formatEvents = (events) => {
    return events.map(event => {
      const fromDate = event.from_date.slice(0, 4) + '-' + event.from_date.slice(4, 6) + '-' + event.from_date.slice(6, 8);
      const toDate = event.to_date.slice(0, 4) + '-' + event.to_date.slice(4, 6) + '-' + event.to_date.slice(6, 8);
      const fromTime = event.from_time ? 'T' + event.from_time : '';
      const toTime = event.to_time ? 'T' + event.to_time : '';
  
      return {
        title: event.title,
        start: new Date(fromDate + fromTime),
        end: new Date(toDate + toTime),
        allDay: !event.from_time && !event.to_time,
        description: event.description,
        venue: event.venue,
        type: event.type,
        category: event.category,
        link: event.location,
        legend_color: event.legend_color,
        text_color: event.text_color,
        details_url: event.details_url
      };
    });
  };

  /**
   * Handler for view change
   * @param {string} view - The new view selected (e.g., 'month', 'week', 'day')
   */
  const onViewChange = (view) => {
    if (view === 'month') {
      triggetCalculateEventHeight();
    }
  };

  /**
   * Calculate the height for each event on the calendar
   */
  const calculateEventHeight = () => {
    if (calendarRef.current) {
      const monthRowElements = calendarRef.current.querySelectorAll(".rbc-month-row");
      if (monthRowElements.length > 0) {
        let monthRowHeight = monthRowElements[0].offsetHeight;
        const dateRowHeight = document.querySelector(".rbc-date-cell").parentNode.offsetHeight;

        monthRowElements.forEach((monthRowElement) => {
          const oecEvents = monthRowElement.querySelectorAll(".oec-custom-event");
          let calcHeight = monthRowHeight;
          if (oecEvents.length > 0) {
            const rbcRow = monthRowElement.querySelectorAll(".rbc-row");
            let actualRowCount = rbcRow.length - 1;
            const showMoreElem = monthRowElement.querySelector(".rbc-show-more");
            if (showMoreElem) {
              calcHeight = monthRowHeight - showMoreElem.offsetHeight;
              actualRowCount = actualRowCount - 1;
            }
            let eventHeight = (calcHeight - dateRowHeight) / actualRowCount;
            if (eventHeight < 0) {
              eventHeight = 2;
            }
            oecEvents.forEach((eventElem) => {
              eventElem.style.height = `${(eventHeight - 2)}px`;
              let rbcEvent = eventElem.closest(".rbc-event");
              if (rbcEvent) {
                  rbcEvent.style.height = `${eventHeight - 2}px`;
              }
            });
          }
        });
      }
    }
  };


  // Trigger for calculating the event height
  const triggetCalculateEventHeight = () => {
    setTimeout(() => {
      calculateEventHeight();
    }, 100); // Initial delay for the first call
  };

  useEffect(() => {
    // Parse query parameters and set initial filters
    const categoryNames = getCategoryFilterFromUrl();
    if (categoryNames.length > 0) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        queryParamFilters: categoryNames,
      }));
    }
  }, []);

  useEffect(() => {
    const loadCulture = async () => {
      if (translations) {
        const locale = translations.language.replace('_', '-');
        try {
          await import(/* webpackChunkName: "globalize-locale" */ `globalize/lib/cultures/globalize.culture.${locale}.js`);
          setCulture(locale);
        } catch (error) {
          try {
            await import(/* webpackChunkName: "globalize-locale" */ 'globalize/lib/cultures/globalize.culture.en-US.js');
            setCulture('en-US');
          } catch (fallbackError) {
            setCulture('en-US');
          }
        }
      }
    };

    loadCulture();
  }, [translations]);

  useEffect(() => {
    // Fetch events from WordPress REST API
    const formattedDate = formatDate(date);
    fetchEvents(EVENTS_API_URL, formattedDate, setEvents, filters, setRenderEvents);
  }, []);

  useEffect(() => {
    if (translations) {
      const messages = {
        previous: translations.previous,
        next: translations.next,
        today: translations.today,
        month: translations.month,
        week: translations.week,
        work_week: translations.work_week,
        day: translations.day,
        agenda: translations.agenda,
        showMore: translations.showMore,
        eventLinkText: translations.eventLinkText,
        date: translations.date,
        time: translations.time,
        event: translations.event,
        eventVenue: translations.eventVenue,
        eventDescription: translations.eventDescription,
        eventLink: translations.eventLink,
        allDay: translations.allDay,
        yesterday: translations.yesterday,
        tomorrow: translations.tomorrow,
        noEventsInRange: translations.noEventsInRange,
        filterBy: translations.filterBy,
        viewAll: translations.viewAll,
        clearAll: translations.clearAll,
      };
      setMessages(messages);
    }
  }, [translations]);

  useEffect(() => {
    // Run the calculation after the first render
    calculateEventHeight();
    window.addEventListener('resize', calculateEventHeight);
    return () => {
      window.removeEventListener('resize', calculateEventHeight);
    };
  });

  return (
    <div ref={calendarRef} style={{height: '100%', width: '100%'}}>
      <Calendar
        localizer={localizer}
        components={components}
        culture={culture}
        messages={messages}
        views={views}
        formats={formats}
        date={date}
        defaultDate={defaultDate}
        onSelectEvent={onSelectEvent}
        events={formatEvents(renderEvents)}
        onView={onViewChange}
        onNavigate={onNavigate}
      />
    </div>
  );
};

export default EventCalendar;
